import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { Badge } from '@bliks-io/ui';
import type { CapacityBinding } from '@bliks-io/contracts';
import { CapacityCell } from './capacity-cell';
import { ManualCapacityBadge } from './manual-capacity-badge';

interface CapacitySourceRowProps {
  label: string;
  binding: CapacityBinding | null;
  effective: boolean;
  /**
   * Shown instead of the binding when the step has no live recording to
   * extrapolate over. It carries the "manuell erfasst" mark itself, because the
   * {@link CapacityCell} that would otherwise render it never runs.
   */
  fallbackText?: string;
  action?: ReactNode;
}

/**
 * One row of the Kapazitätsquellen breakdown (PRD 8.3): a single source's
 * figure, marked "wirksam" when it is the one that won.
 */
export function CapacitySourceRow({
  label,
  binding,
  effective,
  fallbackText,
  action
}: CapacitySourceRowProps) {
  const { t } = useTranslation();

  return (
    <div
      className={`flex items-start gap-2 rounded-md border px-2 py-1.5 ${
        effective ? 'border-info-line bg-info-surface/40' : 'border-line'
      }`}
    >
      <span className="shrink-0 py-0.5 text-xs text-muted-foreground">{label}</span>
      {/* The value carries up to three trailing marks; at the panel's width they
          wrap onto a second line inside the row rather than clip. */}
      <div className="flex min-w-0 flex-1 flex-wrap items-center gap-x-2 gap-y-1">
        {fallbackText ? (
          <>
            <span className="text-sm text-foreground">{fallbackText}</span>
            <ManualCapacityBadge />
          </>
        ) : (
          <CapacityCell
            capacity={binding}
            confidence={binding?.confidence}
            className="flex-wrap gap-x-1 gap-y-1"
          />
        )}
        {effective && <Badge shape="pill">{t('processes.capacityEffective')}</Badge>}
      </div>
      {action}
    </div>
  );
}
